/**
 * Authentication Persistence Service
 * Handles secure token storage, automatic refresh, and session management
 */

import { logError } from '../utils/errorLogger';

export interface StoredAuthData {
  access_token: string;
  refresh_token: string;
  token_type: string;
  access_token_expires_at: string;
  user: {
    id: string;
    email?: string; // Optional - not stored for security (PII)
    phone?: string; // Optional - not stored for security (PII)
    first_name: string;
    last_name: string;
    role: string;
    roles?: string[]; // Array of all roles user has
    active_role?: string; // Currently active role (from token)
    is_active: boolean;
  };
  remember_me: boolean;
  last_activity: string;
  session_id?: string;
}

export interface AuthPersistenceConfig {
  accessTokenKey: string;
  refreshTokenKey: string;
  userDataKey: string;
  rememberMeKey: string;
  lastActivityKey: string;
  sessionIdKey: string;
  sessionTimeoutMinutes: number;
  inactivityTimeoutMinutes: number;
  autoRefreshThresholdMinutes: number;
}

class AuthPersistenceService {
  private config: AuthPersistenceConfig = {
    accessTokenKey: 'mirsal_access_token',
    refreshTokenKey: 'mirsal_refresh_token',
    userDataKey: 'mirsal_user_data',
    rememberMeKey: 'mirsal_remember_me',
    lastActivityKey: 'mirsal_last_activity',
    sessionIdKey: 'mirsal_session_id',
    sessionTimeoutMinutes: 24 * 60, // 24 hours
    inactivityTimeoutMinutes: 120, // 2 hours (increased from 30 minutes)
    autoRefreshThresholdMinutes: 5, // Refresh token 5 minutes before expiry
  };

  /**
   * Store authentication data securely
   * Note: Tokens are now stored in httpOnly cookies, not in browser storage
   * SECURITY: Only store minimal non-sensitive user data (no email, phone, or PII)
   */
  storeAuthData(data: StoredAuthData): void {
    try {
      // Always use localStorage for user data (accessible across tabs)
      // The remember_me cookie expiration handles session vs persistent behavior
      // Even if remember_me is false, we store in localStorage so new tabs can access it
      const storage = localStorage;
      
      // DO NOT store tokens - they're in httpOnly cookies now
      // SECURITY: Only store minimal non-sensitive data for initial UI display
      // Full user data (including email/phone) will be fetched from API after authentication verification
      
      // Store only minimal non-sensitive user data
      const minimalUserData = {
        id: data.user.id,
        first_name: data.user.first_name,
        last_name: data.user.last_name,
        role: data.user.role,
        active_role: data.user.active_role || data.user.role,
        roles: data.user.roles || [data.user.role],
        is_active: data.user.is_active,
        // DO NOT store: email, phone, created_at, or any other PII
        // These will be fetched from API when needed
      };
      
      storage.setItem(this.config.userDataKey, JSON.stringify(minimalUserData));
      
      // Store session ID if provided
      if (data.session_id) {
        storage.setItem(this.config.sessionIdKey, data.session_id);
      }
      
      // Don't store remember_me in localStorage - it's in a cookie now
      // Keep this for migration compatibility only
      localStorage.setItem(this.config.rememberMeKey, JSON.stringify(data.remember_me));
      
      // Update last activity
      this.updateLastActivity();
    } catch (error) {
      logError('Failed to store auth data', error);
    }
  }

  /**
   * Retrieve stored authentication data
   * Note: Tokens are in httpOnly cookies and not accessible to JavaScript
   */
  getStoredAuthData(): StoredAuthData | null {
    try {
      // Always check localStorage first (accessible across tabs)
      // Check sessionStorage as fallback for migration compatibility
      let userData = localStorage.getItem(this.config.userDataKey);
      let storage = localStorage;
      
      // If no data found in localStorage, check sessionStorage (migration fallback)
      if (!userData) {
        const sessionData = sessionStorage.getItem(this.config.userDataKey);
        if (sessionData) {
          userData = sessionData;
          storage = sessionStorage;
          // Migrate to localStorage for cross-tab access
          localStorage.setItem(this.config.userDataKey, sessionData);
        } else {
          return null;
        }
      }

      const user = JSON.parse(userData);
      const lastActivity = localStorage.getItem(this.config.lastActivityKey);
      const sessionId = storage.getItem(this.config.sessionIdKey);
      
      // Get remember_me from cookie (not localStorage)
      const rememberMe = this.getRememberMePreference();

      // Ensure user data is minimal (migrate old full user data if needed)
      const minimalUser = {
        id: user.id,
        first_name: user.first_name,
        last_name: user.last_name,
        role: user.role,
        active_role: user.active_role || user.role,
        roles: user.roles || [user.role],
        is_active: user.is_active,
        // Explicitly exclude email, phone, and other PII
        // These should not be in storage
      };

      return {
        // Tokens are in cookies - not accessible to JavaScript
        access_token: '',
        refresh_token: '',
        token_type: 'Bearer',
        access_token_expires_at: '', // Will be updated from API responses if needed
        user: minimalUser,
        remember_me: rememberMe, // From cookie, not localStorage
        last_activity: lastActivity || new Date().toISOString(),
        session_id: sessionId || undefined,
      };
    } catch (error) {
      logError('Failed to retrieve auth data', error);
      return null;
    }
  }

  /**
   * Check if current session is valid
   */
  isSessionValid(): boolean {
    const authData = this.getStoredAuthData();
    if (!authData) return false;

    // Check token expiry
    if (this.isTokenExpired(authData.access_token_expires_at)) {
      return false;
    }

    // Check inactivity timeout (only for non-remember me sessions)
    if (!authData.remember_me && this.isInactivityTimeoutReached(authData.last_activity)) {
      return false;
    }

    return true;
  }

  /**
   * Check if token needs refresh
   */
  shouldRefreshToken(): boolean {
    const authData = this.getStoredAuthData();
    if (!authData) return false;

    const expiryTime = new Date(authData.access_token_expires_at);
    const now = new Date();
    const minutesUntilExpiry = (expiryTime.getTime() - now.getTime()) / (1000 * 60);

    return minutesUntilExpiry <= this.config.autoRefreshThresholdMinutes;
  }

  /**
   * Update last activity timestamp
   */
  updateLastActivity(): void {
    const timestamp = new Date().toISOString();
    localStorage.setItem(this.config.lastActivityKey, timestamp);
  }

  /**
   * Clear all stored authentication data
   * Note: Cookies are cleared by backend on logout
   */
  clearAuthData(): void {
    try {
      // Clear user data from both storages
      [localStorage, sessionStorage].forEach(storage => {
        storage.removeItem(this.config.userDataKey);
        storage.removeItem(this.config.sessionIdKey);
      });

      // Clear preferences and activity
      localStorage.removeItem(this.config.rememberMeKey);
      localStorage.removeItem(this.config.lastActivityKey);
      
      // Note: Tokens in httpOnly cookies are cleared by backend on logout
    } catch (error) {
      logError('Failed to clear auth data', error);
    }
  }

  /**
   * Get remember me preference from cookie (not localStorage)
   * This ensures the preference is accessible across tabs
   */
  getRememberMePreference(): boolean {
    try {
      // Read from cookie first (accessible across tabs)
      const cookies = document.cookie.split(';');
      const rememberMeCookie = cookies.find(cookie => 
        cookie.trim().startsWith(`${this.config.rememberMeKey}=`)
      );
      
      if (rememberMeCookie) {
        const value = rememberMeCookie.split('=')[1]?.trim();
        return value === 'true';
      }
      
      // Fallback to localStorage for migration compatibility
      const preference = localStorage.getItem(this.config.rememberMeKey);
      return preference ? JSON.parse(preference) : false;
    } catch {
      return false;
    }
  }

  /**
   * Update stored tokens after refresh
   * Note: Tokens are now in httpOnly cookies, so this method is a no-op
   * This method is kept for API compatibility but does not store tokens
   * Tokens are managed by backend via httpOnly cookies
   */
  updateTokens(accessToken: string, refreshToken?: string, expiresAt?: string): void {
    // Tokens are now in httpOnly cookies, updated by backend automatically
    // This method is a no-op - tokens cannot be accessed or stored in JavaScript
    // Just update last activity timestamp
    try {
      this.updateLastActivity();
    } catch (error) {
      logError('Failed to update last activity', error);
    }
  }

  /**
   * Check if token is expired
   */
  private isTokenExpired(expiresAt: string): boolean {
    try {
      const expiryTime = new Date(expiresAt);
      const now = new Date();
      return now >= expiryTime;
    } catch {
      return true; // If we can't parse the date, consider it expired
    }
  }

  /**
   * Check if inactivity timeout is reached
   */
  private isInactivityTimeoutReached(lastActivity: string): boolean {
    try {
      const lastActivityTime = new Date(lastActivity);
      const now = new Date();
      const minutesInactive = (now.getTime() - lastActivityTime.getTime()) / (1000 * 60);
      
      return minutesInactive > this.config.inactivityTimeoutMinutes;
    } catch {
      return true; // If we can't parse the date, consider it timed out
    }
  }

  /**
   * Calculate token expiry from JWT token
   */
  private calculateTokenExpiry(token: string): string {
    try {
      // Decode JWT token to get expiry
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      
      const payload = JSON.parse(jsonPayload);
      
      if (payload.exp) {
        return new Date(payload.exp * 1000).toISOString();
      }
    } catch (error) {
      logError('Failed to decode token', error);
    }
    
    // Fallback: assume 8 hours expiry (matching backend settings)
    const fallbackExpiry = new Date();
    fallbackExpiry.setHours(fallbackExpiry.getHours() + 8);
    return fallbackExpiry.toISOString();
  }

  /**
   * Get current authentication status
   */
  getAuthStatus(): {
    isAuthenticated: boolean;
    needsRefresh: boolean;
    user: any;
    rememberMe: boolean;
  } {
    const authData = this.getStoredAuthData();
    const isValid = this.isSessionValid();
    
    return {
      isAuthenticated: isValid && !!authData,
      needsRefresh: !!authData && this.shouldRefreshToken(),
      user: authData?.user || null,
      rememberMe: authData?.remember_me || false,
    };
  }

  /**
   * Initialize session monitoring
   */
  initializeSessionMonitoring(onSessionExpired: () => void, onTokenRefreshNeeded: () => void): (() => void) {
    // Check session status every minute
    const sessionCheckInterval = setInterval(() => {
      if (!this.isSessionValid()) {
        clearInterval(sessionCheckInterval);
        onSessionExpired();
        return;
      }

      if (this.shouldRefreshToken()) {
        onTokenRefreshNeeded();
      }
    }, 60 * 1000); // Check every minute

    // Update activity on user interactions
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const updateActivity = () => {
      this.updateLastActivity();
    };

    activityEvents.forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });

    // Cleanup function
    return () => {
      clearInterval(sessionCheckInterval);
      activityEvents.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
    };
  }
}

export const authPersistence = new AuthPersistenceService();
export default authPersistence;
